import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { OrgVersionSelector } from '@client/components/obligations/OrgVersionSelector';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useIsUIDebugInfoEnabled } from '@client/hooks/system/useIsUIDebugInfoEnabled';
import { ICONS } from '@client/styles/theme';
import { trpc } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { exportObligation } from '@client/utils';
import { createFileRoute } from '@tanstack/react-router';
import { Drawer, Skeleton } from 'antd';
import { Bot, Download } from 'lucide-react';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { ObligationBreaches } from './ObligationBreaches';
import { obligationFormStore } from './store';

export const Route = createFileRoute('/console/$orgId/compliance/obligations/details/$id')({
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    staticData: { ui: { widthVariant: 'default' }, entity: 'obligation' },
    beforeLoad({ context: { trpcUtils }, params: { id } }): ShareableRouteContext {
        async function title() {
            const obligation = await trpcUtils.obligation.getObligation.ensureData({ id });
            return <span>Obligation Detail • {obligation.identificationCodeWithPrefix}</span>;
        }

        async function breadcrumb() {
            const obligation = await trpcUtils.obligation.getObligation.ensureData({ id });
            return `Obligation detail • ${obligation.identificationCodeWithPrefix}`;
        }

        return {
            ui: {
                title,
            },
            breadcrumb: {
                title: breadcrumb,
            },
        };
    },
    component: ObligationDetails,
});

type ObligationBreachesAction = ['none'] | ['view', bigint];

function ObligationDetails() {
    const [obligationBreachesAction, setObligationBreachesAction] = useState<ObligationBreachesAction>(['none']);
    const updateObligationMutation = trpc.obligation.updateObligation.useMutation();
    const { trpcClient, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const orgId = useOrgId();
    const { id } = Route.useParams();
    const isDebugInfoEnabled = useIsUIDebugInfoEnabled();

    // Fetch obligation data with tRPC query
    const obligationQuery = trpc.obligation.getObligation.useQuery(
        { id },
        {
            enabled: !!id,
            throwOnError(error) {
                match(error.data?.errorId)
                    .with('recordNotFound', () => {
                        void errorMessage
                            .showAsync(
                                "The obligation you are trying to access either doesn't exist or you don't have permissions to access it.",
                            )
                            .then(() => {
                                navigate({
                                    to: '/console/$orgId/compliance/obligations',
                                    params: { orgId },
                                });
                            });
                    })
                    .otherwise(() => {
                        errorMessage.show(error);
                    });
                return false;
            },
        },
    );

    const { formData: obligation } = obligationFormStore.useInit({
        entity: 'obligation',
        data: obligationQuery.data,
        isLoading: obligationQuery.isLoading,

        async onReload() {
            await obligationQuery.refetch();
        },

        async onSave(currentValue, newValue) {
            try {
                await updateObligationMutation.mutateAsync({
                    id: currentValue.id,
                    set: newValue,
                });

                await trpcUtils.obligation.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    return (
        <PageController route={Route}>
            <Drawer
                open={obligationBreachesAction[0] === 'view'}
                width="70%"
                onClose={() => setObligationBreachesAction(['none'])}
                title="Breaches"
                destroyOnHidden
            >
                {obligationBreachesAction[0] === 'view' && (
                    <ObligationBreaches obligationId={obligationBreachesAction[1]} />
                )}
            </Drawer>

            {obligation && id === obligation.id ? (
                <>
                    <div className="mb-4">
                        <OrgVersionSelector
                            currentId={id}
                            currentVersion={obligation.version}
                            currentStatus={obligation.status}
                            identificationCode={obligation.identificationCode ?? null}
                            currentMasterObligationId={obligation.masterObligationId ?? null}
                        />
                    </div>
                    <Tabs
                        rightAddon={
                            <Button
                                type="secondary"
                                icon={<Download />}
                                onClick={() => void exportObligation(trpcClient, id)}
                            >
                                Download obligation
                            </Button>
                        }
                        primaryActionAddon={
                            <Button type="primary" onClick={() => setObligationBreachesAction(['view', obligation.id])}>
                                View breaches
                            </Button>
                        }
                        items={[
                            {
                                label: (
                                    <span className="flex items-center gap-2">
                                        <ICONS.manage size={16} />
                                        Manage obligation
                                    </span>
                                ),
                                to: '/console/$orgId/compliance/obligations/details/$id/manage',
                            },
                            ...(isDebugInfoEnabled
                                ? [
                                      {
                                          label: (
                                              <span className="flex items-center gap-2">
                                                  <Bot size={16} />
                                                  Debug (Embeddings)
                                              </span>
                                          ),
                                          to: '/console/$orgId/compliance/obligations/details/$id/debug' as const,
                                      },
                                  ]
                                : []),
                        ]}
                    />
                </>
            ) : (
                <Skeleton active />
            )}
        </PageController>
    );
}
